// Controlled state for the academic-config controls (PRD-38).
//
// Deliberately NOT react-hook-form: the type→count prefill has to fire from
// the period-type change handler and from nowhere else, and plain state makes
// that impossible to get wrong. There is no effect in this file, so a
// hand-typed `periods_per_cycle` can never be clobbered by a re-render.

import {
  academicConfigSchema,
  academicOverrideSchema,
  normalizeOverride,
  type AcademicConfigValues,
  type AcademicOverrideValues,
} from "@lib/academicConfig"
import { useCallback, useState } from "react"

/** Only `periods_per_cycle` is free-text, so a failure always belongs to it. */
function firstIssueCode(issues: { message: string }[]): string {
  return issues[0]?.message ?? "invalid"
}

export interface AcademicConfigState {
  values: AcademicConfigValues
  change: (patch: Partial<AcademicConfigValues>) => void
  /** Error CODE for `periods_per_cycle`; map via `common:academic.error.*`. */
  errorCode: string | undefined
  /** Parsed values, or null when invalid (and `errorCode` is now set). */
  validate: () => AcademicConfigValues | null
}

export function useAcademicConfig(initial: AcademicConfigValues): AcademicConfigState {
  const [values, setValues] = useState<AcademicConfigValues>(initial)
  const [errorCode, setErrorCode] = useState<string | undefined>(undefined)

  const change = useCallback((patch: Partial<AcademicConfigValues>) => {
    setValues((prev) => ({ ...prev, ...patch }))
    setErrorCode(undefined)
  }, [])

  const validate = useCallback((): AcademicConfigValues | null => {
    const parsed = academicConfigSchema.safeParse(values)
    if (parsed.success) {
      setErrorCode(undefined)
      return parsed.data
    }
    setErrorCode(firstIssueCode(parsed.error.issues))
    return null
  }, [values])

  return { values, change, errorCode, validate }
}

export interface AcademicOverrideState {
  values: AcademicOverrideValues
  change: (patch: Partial<AcademicOverrideValues>) => void
  errorCode: string | undefined
  validate: () => AcademicOverrideValues | null
  /** True when at least one field is overridden (i.e. not inheriting). */
  hasOverride: boolean
}

export function useAcademicOverride(initial: AcademicOverrideValues): AcademicOverrideState {
  const [values, setValues] = useState<AcademicOverrideValues>(initial)
  const [errorCode, setErrorCode] = useState<string | undefined>(undefined)

  const change = useCallback((patch: Partial<AcademicOverrideValues>) => {
    setValues((prev) => ({ ...prev, ...patch }))
    setErrorCode(undefined)
  }, [])

  const validate = useCallback((): AcademicOverrideValues | null => {
    const parsed = academicOverrideSchema.safeParse(normalizeOverride(values))
    if (parsed.success) {
      setErrorCode(undefined)
      return parsed.data
    }
    setErrorCode(firstIssueCode(parsed.error.issues))
    return null
  }, [values])

  const normalized = normalizeOverride(values)
  const hasOverride =
    normalized.period_type !== null ||
    normalized.periods_per_cycle !== null ||
    normalized.grade_scale !== null

  return { values, change, errorCode, validate, hasOverride }
}
